const {V} = require("./VectorDraw");
const _ = require("lodash");
const Font = require("../../util/Font");
const ColorUtil = require("../../util/ColorUtil");
const DrawingUtil = require("../../util/DrawingUtil");
const StringUtil = require("../../../../shared/StringUtil");
const CanvasMath = require("../CanvasMath");
const Common = require("./CommonHelpers");
const cs = ColorUtil.getCurrentScheme();
const colors = ColorUtil.getDiagramColors();
const Fractions = require("../../util/Fractions");

const makeGraph = function(
	ctx,
	width,
	xPadding,
	xTitle,
	yTitle,
	yMax,
	xLabels
) {
	const maxXLabelWidth = _.max(
		_.map(xLabels, elem => Common.measureBatex(ctx, elem).width)
	);
	const height = ctx.getSize().height;
	const NUM_VERT_LABELS = 5;
	const yTitleHeight = Common.measureBatex(ctx, yTitle).height * 0.6;
	const xTitleHeight = (Common.measureBatex(ctx, xTitle).height * height) / 250;
	const yLabels = _.map(
		_.range(NUM_VERT_LABELS + 1),
		l => "" + (yMax * l) / NUM_VERT_LABELS
	);
	const maxYLabelWidth = _.max(
		_.map(yLabels, l => Common.measureBatex(ctx, l).width / 2)
	);
	//distance before the start of the graph
	const xStart = yTitleHeight + maxYLabelWidth + 2.5 * xPadding;
	const titleFontSize = Math.min(xPadding * 10, height / 10, 20);
	const betweenLabels = (width - xStart) / (xLabels.length + 1);
	//20*betweenLabels/maxXLabelWidth would fill the space below a bar; we want some padding
	const labelFontSize = Math.min(
		(0.5 * height) / NUM_VERT_LABELS,
		(19.5 * betweenLabels) / maxXLabelWidth,
		18
	);
	const labelBatexProps = {
		scaleDownOnly: true,
		fontInfo: {size: labelFontSize},
	};
	const graphHeight = 0.95 * height - xTitleHeight - labelFontSize;
	const yStart = labelFontSize / 2;
	let numParts = 4;
	while (numParts > 1) {
		if (yMax % numParts === 0) {
			break;
		}
		numParts--;
	}
	if (yMax % 25 === 0 && numParts === 1) {
		numParts = 5;
	}

	ctx.save();
	ctx.changeFontSize(titleFontSize);
	Common.drawBatexAtCenter(
		ctx,
		xTitle,
		(xStart + width) / 2,
		height - (2 * xTitleHeight) / 3,
		0
	);
	Common.drawBatexAtCenter(
		ctx,
		yTitle,
		yTitleHeight / 2 + xPadding / 2,
		yStart + (graphHeight - yStart) / 2,
		270
	);

	_.each(xLabels, (l, i) => {
		const x = xStart + (i + 0.55) * betweenLabels;
		Common.drawBatexInRect(
			ctx,
			l,
			x,
			graphHeight + height / 40,
			0.9 * betweenLabels,
			labelFontSize,
			labelBatexProps
		);
	});

	//draw lines and insert labels for the y-axis
	_.each(yLabels, (elem, x) => {
		const vertPos = yStart + (graphHeight - yStart) * (1 - x / NUM_VERT_LABELS);
		let adjustedHeight = vertPos - labelFontSize / 2;
		Common.drawBatexInRect(
			ctx,
			elem,
			yTitleHeight,
			adjustedHeight,
			xStart - yTitleHeight,
			labelFontSize,
			labelBatexProps
		);
		ctx.save();
		ctx
			.bp()
			.M(xStart, vertPos)
			.H(width)
			.stroke(colors.defaultblack);
		if (x !== NUM_VERT_LABELS) {
			for (let y = 1; y < numParts; y++) {
				adjustedHeight =
					vertPos - ((graphHeight - yStart) * y) / (NUM_VERT_LABELS * numParts);
				ctx
					.bp()
					.M(xStart, adjustedHeight)
					.H(width)
					.stroke(cs.lightGrayFill);
			}
		}
		ctx.restore();
	});
	ctx
		.bp()
		.M(xStart, yStart)
		.V(graphHeight)
		.stroke(colors.defaultblack);
	ctx
		.bp()
		.M(width - ctx.lineWidth, yStart)
		.V(graphHeight)
		.stroke(colors.defaultblack);
	ctx.restore();
	return {graphHeight, yStart, xStart, betweenLabels};
};

const makeCircleDiagram = function(ctx, circles, lines, labels = []) {
	const edgePadding = 5;
	const radius = 30;
	const blankBatex = "$\\blank{2}$";
	const min = [circles[0][0], circles[0][1]];
	const max = [circles[0][0], circles[0][1]];
	for (let i = 0; i < circles.length; i++) {
		min[0] = Math.min(min[0], circles[i][0]);
		min[1] = Math.min(min[1], circles[i][1]);
		max[0] = Math.max(max[0], circles[i][0]);
		max[1] = Math.max(max[1], circles[i][1]);
	}
	const w = max[0] - min[0];
	const h = max[1] - min[1];
	const size = ctx.getSize();
	const ctxW = size.width - radius * 2 - edgePadding * 2;
	const ctxH = size.height - radius * 2 - edgePadding * 2;
	const realW = Math.min(ctxW, (ctxH * w) / h);
	const realH = (realW * h) / w;

	const tr = function(p) {
		return V(
			radius + edgePadding + ((p[0] - min[0]) * realW) / w,
			radius + edgePadding + ((p[1] - min[1]) * realH) / h
		);
	};

	ctx.save();
	lines.forEach(line => {
		const p1 = circles[line[0]];
		const p2 = circles[line[1]];
		ctx
			.bp()
			.M(tr(p1))
			.L(tr(p2))
			.stroke(colors.defaultblack, 4);
		//draws line label if it exists in line[2]
		if (line[2]) {
			const mPoint = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
			const dist = Math.pow(
				Math.pow(p2[1] - p1[1], 2) + Math.pow(p2[0] - p1[0], 2),
				0.5
			);
			const yMult = line[0] < line[1] ? -0.7 : 0.7;
			const xMult = (p2[1] - p1[1]) * (p2[0] - p1[0]) > 0 ? -yMult : yMult;
			const direction = [
				(xMult * Math.abs(p2[1] - p1[1])) / dist,
				(yMult * Math.abs(p2[0] - p1[0])) / dist,
			];
			Common.label(ctx, line[2], tr(mPoint)[0], tr(mPoint)[1], direction);
		}
	});

	circles.forEach(circ => {
		const center = tr(circ);
		const circleColor = circ[3] || "white";
		ctx
			.bp()
			.arc(center[0], center[1], radius, 0, Math.PI * 2)
			.stroke(colors.defaultblack, 3)
			.fill(colors[circleColor]);
		ctx.fillStyle = colors.defaultblack;
		center.label(ctx, circ[2] || blankBatex, V(0, 0));
	});

	labels.forEach(l => {
		const lp = tr(l);
		ctx.save();
		ctx.fillStyle = colors.defaultblack;
		lp.label(ctx, l[2], V(0, 0));
		ctx.restore();
	});

	ctx.restore();
};

module.exports = {
	/**
	 * Creates an amorphous blob that passes through the provided set of points.
	 * arr is in the form [x1, y1, x2, y2, ...]
	 * smoothness allows the user to specify how smooth the blob will be: higher smoothness will be smoother but will include more radical bends.
	 * settings allows the user to change the color of the outline or fill of the blob.
	 **/
	irregularBlob: function(ctx, arr, smoothness, settings) {
		settings = _.extend(
			{fillCol: null, strokeCol: colors.defaultblack},
			settings
		);
		const {fillCol, strokeCol} = settings;

		const rref = require("rref");
		//inputs two two-element arrays representing points and the derivative at each point and draws a line between them
		const drawCurve = function(elem, next, d1, d2) {
			let mat = [
				[elem[0] * elem[0] * elem[0], elem[0] * elem[0], elem[0], 1, elem[1]],
				[next[0] * next[0] * next[0], next[0] * next[0], next[0], 1, next[1]],
				[3 * elem[0] * elem[0], 2 * elem[0], 1, 0, d1],
				[3 * next[0] * next[0], 2 * next[0], 1, 0, d2],
			];
			mat = rref(mat);
			const c = mat[0][4];
			const q = mat[1][4];
			const v = mat[2][4];
			const z = mat[3][4];
			// const x = (d2 - (elem[1] - next[1]) / (elem[0] - next[0])) / ((next[0] - elem[0]) * (next[0] - elem[0]));
			// const y = (d1 - (elem[1] - next[1]) / (elem[0] - next[0])) / ((next[0] - elem[0]) * (next[0] - elem[0]));
			// const c = x + y;
			// const q = x * (-2 * elem[0] - next[0]) - y * (2 * next[0] + elem[0]);
			// const v = (elem[1] - next[1]) / (elem[0] - next[0]) + elem[0] * elem[0] * x + 2 * elem[0] * next[0] * x + next[0] * next[0] * y + 2 * elem[0] * next[0] * y;
			// const z = (elem[0] * next[1] - elem[1] * next[0]) / (elem[0] - next[0]) - elem[0] * elem[0] * x * next[0] - elem[0] * next[0] * next[0] * y;
			const evaluate = function(n) {
				return c * n * n * n + q * n * n + v * n + z;
			};
			if (elem[0] < next[0]) {
				for (let n = elem[0]; n <= next[0]; n++) {
					ctx.L(n, evaluate(n));
				}
			} else {
				for (let n = elem[0]; n >= next[0]; n--) {
					ctx.L(n, evaluate(n));
				}
			}
		};

		const pointOf = i => [
			arr[(i + arr.length) % arr.length],
			arr[(i + arr.length + 1) % arr.length],
		];
		const nextSlope = function(curr, next, nextNext) {
			const left =
				smoothness *
				(nextNext[1] > curr[1] ? 1 : -1) *
				(curr[0] > next[0] ? -1 : 1);
			return (next[0] - curr[0]) * (next[0] - nextNext[0]) < 0
				? (nextNext[1] - next[1]) / (nextNext[0] - next[0])
				: left;
		};
		ctx.bp().M(arr[0], arr[1]);
		for (let i = 0; i < arr.length; i += 2) {
			const prev = pointOf(i - 2);
			const curr = pointOf(i);
			const next = pointOf(i + 2);
			const nextNext = pointOf(i + 4);
			//normal point
			if ((prev[0] - curr[0]) * (next[0] - curr[0]) < 0) {
				drawCurve(
					curr,
					next,
					(next[1] - prev[1]) / (next[0] - prev[0]),
					nextSlope(curr, next, nextNext)
				);
			} else if (curr[0] === next[0]) {
				ctx.L(next[0], next[1]);
			} else {
				const left =
					smoothness *
					(curr[0] > prev[0] ? 1 : -1) *
					(next[1] > prev[1] ? -1 : 1);
				drawCurve(curr, next, left, nextSlope(curr, next, nextNext));
			}
		}
		ctx.save();
		if (fillCol) {
			ctx.fill(fillCol);
		}
		ctx.stroke(strokeCol);
		ctx.restore();
	},

	/**
	 *  Pseudo-engine for the Missing Numbers problems in Grade 3 Chapter 8
	 *  This function takes in the diagram context, a list of circle coordinates,
	 *  a list of lines, and some optional labels.
	 **/
	divisionMissingNumbers: function(ctx, circles, lines, labels) {
		makeCircleDiagram(ctx, circles, lines, labels);
	},

	/**
	 *  Pseudo-engine for the Divisibility Web Puzzles in 4-5-13
	 *  This function takes in the diagram context, a list of circle coordinates,
	 *  and a list of lines with labels.
	 **/
	divisibilityWeb: function(ctx, circles, lines) {
		makeCircleDiagram(ctx, circles, lines);
	},

	/**
	 *  Pseudo-engine for the dominoes problems in Grade 4 Chapter 4
	 **/
	dominoes: function(ctx, grid, answer, rowClues, colClues) {
		const lowerCaseGrid = grid.map(row => row.map(c => c.toLowerCase()));
		const numRows = grid.length;
		const numCols = numRows + 1;
		const blankBatex = "$\\blank{1}$";
		const size = ctx.getSize();
		const ctxW = size.width;
		const ctxH = size.height;
		const hPadding = ctxW / 7;
		const vPadding = (ctxH - (numRows * (ctxW - 2 * hPadding)) / numCols) / 2;
		const cellLength = (ctxW - 2 * hPadding) / numCols;
		const strokeThickness = 3;
		let i;
		let j;

		// [i, j] returns top left point of that cell in the domino grid, with [0,0] being
		// the top left cell.
		const tr = function(p) {
			return V(hPadding + p[0] * cellLength, vPadding + p[1] * cellLength);
		};
		ctx.save();
		ctx.fillStyle = colors.defaultblack;
		// Draw row clues
		for (i = 0; i < rowClues.length; i++) {
			if (rowClues[i]) {
				const center = V(hPadding / 2, vPadding + (i + 1 / 2) * cellLength);
				center.label(ctx, rowClues[i], V(0, 0));
			}
		}
		// Draw column clues
		for (i = 0; i < colClues.length; i++) {
			if (colClues[i]) {
				const center = V(hPadding + (i + 1 / 2) * cellLength, vPadding / 2);
				center.label(ctx, colClues[i], V(0, 0));
			}
		}
		// Draw batex in each cell.
		for (j = 0; j < grid.length; j++) {
			for (i = 0; i < grid[j].length; i++) {
				const center = tr([i + 0.5, j + 0.5]);
				const batex =
					grid[j][i] !== lowerCaseGrid[j][i] ? answer[j][i] : blankBatex;
				center.label(ctx, batex, V(0, 0));
			}
		}
		ctx.bp();
		// Draw grid boundaries.
		ctx
			.M(hPadding - strokeThickness / 2, vPadding)
			.L(ctxW - hPadding, vPadding)
			.L(ctxW - hPadding, ctxH - vPadding)
			.L(hPadding, ctxH - vPadding)
			.L(hPadding, vPadding);
		// Draw left vertical lines of each domino.
		for (j = 0; j < grid.length; j++) {
			for (i = 0; i < grid[j].length; i++) {
				ctx.M(tr([i, j])).L(tr([i, j + 1]));
				if (lowerCaseGrid[j][i] === "h") {
					i++;
				}
			}
		}
		// Draw top horizontal lines of each domino.
		for (i = 0; i < grid[0].length; i++) {
			for (j = 0; j < grid.length; j++) {
				ctx.M(tr([i, j])).L(tr([i + 1, j]));
				if (lowerCaseGrid[j][i] === "v") {
					j++;
				}
			}
		}
		ctx.stroke(colors.defaultblack, strokeThickness);
		ctx.restore();
	},

	/**
	 * Creates a line graph modeling the data provided.
	 * data is an array holding objects, each of which represents a single line on the graph of the form:
	 * * keyString: the word associated with the line in the key
	 * * color: the color the line should be
	 * * dashed: boolean holding whether the line should be solid (false) or dashed (true)
	 * * heights: an array in which the nth number corresponds to the height of this line in the nth category
	 * labels is an array holding what the x-axis labels should be. There should be one per height.
	 * xTitle is the title under the x-axis.
	 * yTitle is the title left of the y-axis.
	 **/
	lineGraph: function(ctx, data, labels, xTitle, yTitle) {
		const yMax =
			5 * Math.ceil(_.max(_.map(data, elem => _.max(elem.heights))) / 5);
		let width = ctx.getSize().width * 0.7;
		const {graphHeight, yStart, xStart, betweenLabels} = makeGraph(
			ctx,
			width,
			width / 28,
			xTitle,
			yTitle,
			yMax,
			labels
		);

		//create lines
		const r = 3;
		// pass in strings "dotted", "dashed", "solid" for line of that type
		// for backwards compatibility, "true" strings or truthy values give a dotted line
		const getLineType = function(stroke) {
			if (stroke) {
				if (typeof stroke === "string") {
					const lineStroke = stroke.toLowerCase();
					if (
						lineStroke === "dotted" ||
						StringUtil.ensureBoolStringConvention(lineStroke) === "true"
					) {
						return "dotted";
					} else if (lineStroke === "dashed") {
						return "dashed";
					} else {
						return "solid";
					}
				} else {
					return "dotted";
				}
			} else {
				return "solid";
			}
		};

		const lineColors = [
			colors.cforange,
			colors.cfpink,
			colors.cfblue,
			colors.cfpurple,
		];
		const lineStrokes = ["dashed", "dotted", "solid"];

		for (let lineIndex = 0; lineIndex < data.length; lineIndex++) {
			const elem = data[lineIndex];
			elem.color = lineColors[lineIndex % 4];
			elem.dashed = lineStrokes[lineIndex % 3];
			ctx.save();
			let prev = yStart + (graphHeight - yStart) * (1 - elem.heights[0] / yMax);
			ctx
				.bp()
				.arc(xStart + betweenLabels, prev, r, 0, 2 * Math.PI)
				.fill(elem.color);
			let dashedArray = [];
			switch (getLineType(elem.dashed)) {
				case "dotted":
					dashedArray = [2];
					break;
				case "dashed":
					dashedArray = [10, 5];
					break;
				default:
					break;
			}
			for (let i = 1; i < labels.length; i++) {
				const x = xStart + (i + 1) * betweenLabels;
				const y =
					yStart + (graphHeight - yStart) * (1 - elem.heights[i] / yMax);
				// white background behind lines
				ctx.setLineDash([]);
				ctx.lineWidth = 6;
				ctx
					.bp()
					.M(x - betweenLabels, prev)
					.L(x, y)
					.stroke("#ffffff");
				ctx.lineWidth = 3;
				ctx.setLineDash(dashedArray);
				ctx
					.bp()
					.arc(x, y, r, 0, 2 * Math.PI)
					.fill(elem.color);
				ctx
					.bp()
					.arc(x - betweenLabels, prev, r, 0, 2 * Math.PI)
					.fill(elem.color);
				ctx
					.bp()
					.M(x - betweenLabels, prev)
					.L(x, y)
					.stroke(elem.color);
				prev = y;
			}
			ctx.restore();
		}

		//make the key box
		//Math.min(0.5 * height / NUM_VERT_LABELS, 19 * betweenLabels / maxXLabelWidth, 18);
		const xKeyStart = (width * 15) / 14;
		width *= 5 / 14;
		const yKeyStart = yStart * 2;
		const keyMeasures = _.map(data, l => Common.measureBatex(ctx, l.keyString));
		const maxKeyWidth = _.max(_.map(keyMeasures, m => m.width));
		const keyProps = {
			scaleDownOnly: true,
			fontInfo: {size: (25 * width) / maxKeyWidth},
		};
		ctx.save();
		ctx
			.bp()
			.rect(xKeyStart, yKeyStart, width - ctx.lineWidth, width)
			.stroke(colors.defaultblack);
		_.each(data, (elem, i) => {
			let y = yKeyStart + ((i + 1) * width) / (data.length + 1);
			ctx
				.bp()
				.arc(xKeyStart + width / 10, y, r, 0, 2 * Math.PI)
				.fill(elem.color);
			ctx
				.bp()
				.arc(xKeyStart + (3 * width) / 10, y, r, 0, 2 * Math.PI)
				.fill(elem.color);
			ctx.save();
			switch (getLineType(elem.dashed)) {
				case "dotted":
					ctx.setLineDash([2]);
					break;
				case "dashed":
					ctx.setLineDash([10, 5]);
					break;
				default:
					break;
			}
			ctx
				.bp()
				.M(xKeyStart + width / 10, y)
				.h(width / 5)
				.stroke(elem.color);
			ctx.restore();
			const x = xKeyStart + (2 * width) / 5;
			y -= keyMeasures[i].height * 0.4;
			Common.drawBatexInRect(
				ctx,
				elem.keyString,
				x,
				y,
				(3 * width) / 5,
				keyMeasures[i].height * 0.8,
				keyProps
			);
		});

		ctx.restore();
	},

	/**
	 * Generates a histogram from the data
	 * arr holds the height of each bar
	 * xTitle is the string to be put below the x-axis.
	 * yTitle is the string to be put below the y-axis.
	 * firstBarMin and firstBarMax hold the starting and ending points of the first bucket.
	 * * If they are integers, the next bucket will start firstBarMax+1; otherwise, it
	 * * will start at firstBarMax.
	 * settings holds extra customizations:
	 * * barColors holds an array of colors. The first color is associated with the first bar,
	 * * * the second with the second, etc. If there are more bars than colors, all the
	 * * * last bars are the last color provided.
	 **/
	histogram: function(
		ctx,
		arr,
		xTitle,
		yTitle,
		firstBarMin,
		firstBarMax,
		settings
	) {
		settings = _.extend(
			{barColors: _.fill(Array(arr.length), colors.mediumDarkGray)},
			settings
		);
		//determine the bounds for each bucket
		const buckets = [];
		const diff =
			firstBarMax -
			firstBarMin +
			(firstBarMin % 1 === 0 && firstBarMax % 1 === 0 ? 1 : 0.1);
		for (let i = 0; i < arr.length; i++) {
			let xLabel = _.round(firstBarMin + i * diff, 1);
			//Check whether the bucket has nonzero width (1-1 looks awkward)
			if (firstBarMin !== firstBarMax) {
				xLabel += "-" + _.round(xLabel + (firstBarMax - firstBarMin), 1);
			}
			buckets.push(xLabel);
		}

		const width = ctx.getSize().width;
		const yMax = 5 * Math.ceil(_.max(arr) / 5);

		const {graphHeight, yStart, xStart, betweenLabels} = makeGraph(
			ctx,
			width,
			width / 40,
			xTitle,
			yTitle,
			yMax,
			buckets
		);

		ctx.save();
		//create bars
		for (let i = 0; i < arr.length; i++) {
			const x = xStart + (i + 0.5) * betweenLabels;
			const y = yStart + (graphHeight - yStart) * (1 - arr[i] / yMax);
			const h = graphHeight - y;
			ctx
				.bp()
				.rect(x, y, betweenLabels, h)
				.stroke(colors.darkGray)
				.fill(settings.barColors[i]);
		}
		ctx.restore();
	},

	/**
	 *  Overlapping circles "engine" for the division chapter
	 *  nums is an array with two parameters for numbers that go in the left and right circles only, respectively.
	 *  answers is an array to fill in the blanks.
	 **/
	overlappingCircles: function(ctx, nums, ans) {
		const nudge = 10;
		const radius = 70;
		const centerDelta = 40;
		const center = V(nudge + radius + centerDelta, nudge + radius);
		ctx.setSize(2 * (nudge + radius + centerDelta), 2 * (nudge + radius));

		const leftCircle = center.add(-centerDelta, 0);
		const rightCircle = center.add(centerDelta, 0);

		const leftText =
			nums && nums[0] !== "" ? "\\center{" + nums[0] + "}\n\n" : "";
		const rightText =
			nums && nums[1] !== "" ? "\\center{" + nums[1] + "}\n\n" : "";
		const leftBlank =
			ans && ans[0] !== "" ? "\\boxed{" + ans[0] + "}\n" : "$\\blank{3}$";
		const centerBlank =
			ans && ans[1] !== "" ? "\\boxed{" + ans[1] + "}\n" : "$\\blank{3}$";
		const rightBlank =
			ans && ans[2] !== "" ? "\\boxed{" + ans[2] + "}\n" : "$\\blank{3}$";
		ctx.lineWidth = 2;
		ctx.bp();
		ctx.arc(leftCircle[0], leftCircle[1], radius, 0, 2 * Math.PI);
		ctx.stroke();
		ctx.bp();
		ctx.arc(rightCircle[0], rightCircle[1], radius, 0, 2 * Math.PI);
		ctx.stroke();
		leftCircle.label(ctx, leftText + leftBlank, V(-0.5, 0));
		center.label(ctx, centerBlank, V(0, 0));
		rightCircle.label(ctx, rightText + rightBlank, V(0.5, 0));
	},

	/**
	 *  Makes a venn diagram with headers and labels
	 *  Can either use default positioning of content in categories or custom positioning
	 *  Data takes in object whose entries are strings or arrays (arrays are list of items): {universal: [1, 2], A: "3", AB: "sample", CA: ["12"]}
	 *  Shaded takes a list of areas that need to be shaded. Areas are made up of distinct areas, ex. "A" only shades category "A" that is not also part of category "B".
	 *  Settings allows the user to specify the boolean hasProgressiveShading and integer numCircles (which can be 1, 2, or 3). By default, numCircles is 2.
	 *  Settings also has a deprecated isThreeWay boolean used to determine whether there are 2 or 3 circles. However,
	 *  this only exists for backwards compatibility - use numCircles instead.
	 *  Settings also allows user to specify boolean universalOff (removes all universal elements and adjusts diagram position when true) and object boundaryLabels
	 *  which adds labels coming off of little lines from each venn diagram circle. Note the latter only renders when universalOff is true.
	 **/

	vennDiagram: function(ctx, labels, data, shaded, settings = {}) {
		DrawingUtil.drawVennDiagram(ctx, {
			radius: 90,
			padding: 5,
			numCircles: settings.numCircles || (settings.isThreeWay ? 3 : 2),
			centerDeltaFactor: settings.centerDeltaFactor,
			labels,
			shaded,
			diagramData: data,
			universalOff: settings.universalOff,
			boundaryLabels: settings.boundaryLabels,
			progressiveShading: settings.hasProgressiveShading,
			bIsSubset: settings.bIsSubset,
			selectedZone: settings.selectedZone || null,
			isDiagram: true,
			getOffsetCoords: (x, y) => V(x, y),
		});
	},

	/**
	 *  Draws pieChart with title, labels, and a key
	 *  Data: an array of data points of the form [label, quantity]
	 *  Settings includes:
	 *   - title: a string that the title should be if there should be one, otherwise null
	 *   - wedgeColors: an array of colors that the wedges should be colored with
	 *   - blackAndWhite: true if the chart should be in black and white
	 *   - label: "percentage"/"quantity"/"fraction"/"normal"
	 *   - legend: "percentage"/"quantity"/"fraction"/"normal"
	 *   - size: "small"/"medium"/"large"
	 **/

	pieChart: function(ctx, data, settings = {}) {
		const numData = data.length;

		let sc = 1; //scaling constant
		if (settings.size === "small") {
			sc = 0.8;
		} else if (settings.size === "large") {
			sc = 1.4;
		}

		//changeable parameters
		const padding = 2 * sc; //the space around the whole graph
		const radius = 100 * sc; //the radius of the pieChart circle
		const titleChartSpacing = 30 * sc; //space between title (if any) and rest of chart
		const legendChartSpacing = 50 * sc; //space between legend (if any) and main graph
		const titleTextSize = 25 * sc;
		const labelTextSize = 17 * sc;
		const labelDist = 20 * sc; //distance of labels to circle
		const labelHorizontalSeg = 20 * sc; //length of horizontal line in label diagram
		const labelTextSpacing = 3 * sc; //min space between labels
		const legendTextSize = 15 * sc;
		const legendTextSpacing = 12 * sc; //space between legend text
		const legendPadding = 20 * sc;
		const defaultWedgeColors = [
			colors.pink,
			colors.orange,
			colors.green,
			colors.blue,
			colors.purple,
		];

		//determine fill colors
		const fillColors = [];
		for (let i = 0; i < numData; i++) {
			let color;
			if (
				!settings.blackAndWhite &&
				settings.hasOwnProperty("wedgeColors") &&
				settings.wedgeColors.length > i
			) {
				color = settings.wedgeColors[i];
			} else if (!settings.blackAndWhite && defaultWedgeColors.length > i) {
				color = defaultWedgeColors[i];
			} else {
				color =
					Math.round(Math.pow(0xff / 0x25, i / numData) * 0x25) * 0x010101; //constant darkness ratio, obeys Weber's Law
			}
			fillColors.push(color);
		}

		//data processing: calculating angles for pie chart
		const angles = []; //list of angles we want to draw lines at
		let sumSoFar = 0;
		const total = _.sumBy(data, d => d[1]);
		for (let i = 0; i < numData; i++) {
			angles.push((sumSoFar / total) * 2 * Math.PI);
			sumSoFar += data[i][1];
		}
		//additional angle of 2pi, used for final sector calculations
		angles.push((sumSoFar / total) * 2 * Math.PI);

		//label processing: compiling labels and legend labels
		const labels = []; //text for labels
		const legendLabels = []; //legend text
		data.forEach(d => {
			const labelText = {
				quantity: d[0] + ", " + d[1],
				percentage: d[0] + ", " + _.round((d[1] / total) * 100, 2) + "%",
				fraction:
					d[0] +
					", " +
					Fractions.makeBatex(
						Fractions.simplifyFraction({numerator: d[1], denominator: total})
					),
				normal: d[0],
			};
			labels.push(labelText[settings.label || "normal"]);
			legendLabels.push(labelText[settings.legend || "normal"]);
		});

		//determine label dimensions and positions (endpoint of segment from midarc) relative to circle center
		const labelProperties = []; //[label x coord, label y coord, label x dim, label y dim]
		const midarcPositions = []; //midpoints of arcs of wedges
		if (settings.hasOwnProperty("label")) {
			const labelSpaceNeeded =
				Common.measureBatex(ctx, labels[0], {size: labelTextSize}).height +
				labelTextSpacing;
			for (let i = 0; i < numData; i++) {
				const midAngle = (angles[i] + angles[i + 1]) / 2;
				const labelTotalDist = radius + labelDist;
				midarcPositions.push([
					-Math.sin(midAngle) * radius,
					-Math.cos(midAngle) * radius,
				]);
				const labelX = -Math.sin(midAngle) * labelTotalDist;
				let labelY = -Math.cos(midAngle) * labelTotalDist;
				if (i > 0) {
					const lastMidAngle = (angles[i - 1] + angles[i]) / 2;
					const sameSide = lastMidAngle < Math.PI === midAngle < Math.PI;
					const lastLabelY = sameSide
						? labelProperties[i - 1][1]
						: labelY + labelSpaceNeeded;
					if (Math.abs(labelY - lastLabelY) < labelSpaceNeeded) {
						labelY =
							lastLabelY +
							(midAngle < Math.PI ? labelSpaceNeeded : -labelSpaceNeeded);
					}
				}
				const labelDims = Common.measureBatex(ctx, labels[i], {
					size: labelTextSize,
				});
				labelProperties.push([
					labelX,
					labelY,
					labelDims.width,
					labelDims.height,
				]);
			}
		}

		//determines maximum spacing relative to circle center that chart (excluding title and legend) needs
		let farthestLeft = -radius;
		let farthestRight = radius;
		let farthestUp = -radius;
		let farthestDown = radius;
		labelProperties.forEach(prop => {
			const labelLength = labelHorizontalSeg + labelTextSpacing + prop[2];
			farthestLeft = Math.min(farthestLeft, prop[0] - labelLength);
			farthestRight = Math.max(farthestRight, prop[0] + labelLength);
			farthestUp = Math.min(farthestUp, prop[1] - prop[3] / 2);
			farthestDown = Math.max(farthestDown, prop[1] + prop[3] / 2);
		});

		//component dimension calculations
		let titleOffset = 0; //total space at top to be reserved for title
		let titleDims = [];
		if (settings.hasOwnProperty("title")) {
			const titleMeasurements = Common.measureBatex(ctx, settings.title, {
				size: titleTextSize,
			});
			titleDims = [titleMeasurements.width, titleMeasurements.height];
			titleOffset = titleDims[1] + titleChartSpacing;
		}

		//legend dimensions calculations
		const legendDims = [0, 0];
		const legendTextHeight = Common.measureBatex(ctx, legendLabels[0], {
			size: legendTextSize,
		}).height;
		let legendTopLeft = [0, 0]; //position of legend with respect to circle center
		const legendCircleRadius = legendTextSize / 2.4; //radius of circles in legend key
		let legendOffset = 0; //total space on right to be reserved for legend
		let legendLabelDims = []; //legend label width and height
		if (settings.hasOwnProperty("legend")) {
			legendDims[1] =
				numData * legendTextHeight +
				(numData - 1) * legendTextSpacing +
				2 * legendPadding;
			legendTopLeft = [
				farthestRight + legendChartSpacing,
				Math.max(-legendDims[1] / 2, farthestUp),
			];
			legendLabelDims = legendLabels.map((label, index) => {
				const ld = Common.measureBatex(ctx, label, {size: legendTextSize});
				return [ld.width, ld.height];
			});
			const longestLegendLabel = _.reduce(
				legendLabelDims,
				(result, legendLabel) => {
					return legendLabel[0] > result ? legendLabel[0] : result;
				},
				0
			);
			legendDims[0] =
				longestLegendLabel +
				2 * legendPadding +
				legendTextSpacing +
				2 * legendCircleRadius;
			legendOffset = legendDims[0] + legendChartSpacing;
		}

		//total graph dimensions calculations
		const totalWidth =
			farthestRight - farthestLeft + legendOffset + 2 * padding;
		const totalHeight =
			-farthestUp +
			Math.max(farthestDown, legendTopLeft[1] + legendDims[1]) +
			titleOffset +
			2 * padding;
		const cx = padding - farthestLeft; //horizontal circle center
		const cy = titleOffset + padding - farthestUp; //vertical circle center

		//begin drawing, write title
		ctx.save();
		ctx.setSize(totalWidth, totalHeight);
		ctx.lineWidth = 1.5;
		if (settings.hasOwnProperty("title")) {
			Common.drawBatexInRect(
				ctx,
				settings.title,
				totalWidth / 2 - titleDims[0] / 2,
				padding,
				titleDims[0],
				titleDims[1],
				{fontInfo: {size: titleTextSize}}
			);
		}

		//draw chart
		ctx.save();
		ctx.lineCap = "round";
		for (let i = 0; i < numData; i++) {
			if (numData !== 1) {
				ctx
					.bp()
					.M(cx, cy)
					.L(
						cx - Math.sin(angles[i + 1]) * radius,
						cy - Math.cos(angles[i + 1]) * radius
					)
					.arc(
						cx,
						cy,
						radius,
						(3 * Math.PI) / 2 - angles[i + 1],
						(3 * Math.PI) / 2 - angles[i]
					)
					// Drawing more lines instead of closePath to prevent sharp tips drawn in origin.
					.L(cx, cy)
					.fill(fillColors[i])
					.stroke(colors.defaultblack);
			} else {
				ctx
					.bp()
					.arc(cx, cy, radius, 0, 2 * Math.PI)
					.fill(fillColors[i])
					.stroke(colors.defaultblack);
			}
		}
		ctx.restore();

		//draw labels
		ctx.fillStyle = colors.darkgray;
		ctx.lineWidth = 1;
		if (settings.hasOwnProperty("label")) {
			for (let i = 0; i < numData; i++) {
				//draw label segments
				const midarc = midarcPositions[i];
				const labelProp = labelProperties[i];
				ctx
					.bp()
					.M(cx + midarc[0], cy + midarc[1])
					.L(cx + labelProp[0], cy + labelProp[1])
					.l((midarc[0] < 0 ? -1 : 1) * labelHorizontalSeg, 0)
					.stroke(colors.defaultblack);
				//write label
				const moveOffset =
					midarc[0] < 0
						? -labelHorizontalSeg - labelProp[2] - labelTextSpacing
						: labelHorizontalSeg + labelTextSpacing;
				Common.drawBatexInRect(
					ctx,
					labels[i],
					cx + labelProp[0] + moveOffset,
					cy + labelProp[1] - labelProp[3] / 2,
					labelProp[2],
					labelProp[3],
					{fontInfo: {size: labelTextSize}}
				);
			}
		}

		//draw legend
		if (settings.hasOwnProperty("legend")) {
			for (let i = 0; i < numData; i++) {
				//draw circles in key
				ctx.lineWidth = 1;
				const legendCircleX =
					cx + legendTopLeft[0] + legendPadding + legendCircleRadius;
				const legendCircleY =
					cy +
					legendTopLeft[1] +
					legendPadding +
					legendTextSpacing * i +
					legendTextHeight * (i + 0.5);
				ctx
					.bp()
					.arc(legendCircleX, legendCircleY, legendCircleRadius, 0, 2 * Math.PI)
					.fill(fillColors[i])
					.stroke(colors.defaultblack);
				//write text
				ctx.fillStyle = colors.darkgray;
				const dims = legendLabelDims[i];
				Common.drawBatexInRect(
					ctx,
					legendLabels[i],
					legendCircleX + legendCircleRadius + legendTextSpacing,
					legendCircleY - legendTextHeight / 2,
					dims[0],
					dims[1],
					{fontInfo: {size: legendTextSize}}
				);
			}
			ctx
				.bp()
				.rect(
					cx + legendTopLeft[0],
					cy + legendTopLeft[1],
					legendDims[0],
					legendDims[1]
				)
				.stroke(colors.defaultblack);
		}

		ctx.restore();
	},

	/**
	 *  Draws pyramid of circles for circle sums.
	 *  Arr is an array of array of numbers or nulls or empty strings that dictate how the pyramid is drawn
	 *  Settings has one setting:
	 *  *  lines: "all" causes it to draw a line between a circle and every possible child instead of only drawing lines if there are two children.
	 *  *  thickness: sets the thickness of the lines between circles
	 *  *  circleThickness: sets the thickness of the lines forming the circles
	 *  *  radius: sets the radius of the circles
	 *  *  squareGrid: adjusts the placement of circles to make the angles of all lines 90*
	 **/
	circleSums: function(ctx, arr, settings = {}) {
		ctx.save();
		const r = settings.radius || 25; //each individual circle's radius
		let dh = r / 2; //horizontal space between circles
		let dv = r / 2; //vertical space between circles
		if (settings.squareGrid) {
			dh = 2 * r;
			dv = 0;
		}
		//Find the length this graph spans
		let maxLeft = 0;
		let maxRight = 0;
		_.forEach(arr, row => {
			const first = _.findIndex(row, i => !_.isNull(i));
			const last = _.findLastIndex(row, i => !_.isNull(i));
			const center = row.length / 2;
			maxLeft = Math.max(maxLeft, center - first + 0.5);
			maxRight = Math.max(maxRight, last - center + 0.5);
		});
		const maxRowLength = maxLeft + maxRight;
		const numRows = arr.length;
		const padding = r / 4;
		const w = maxRowLength * (2 * r + dh) - dh + padding;
		const h = numRows * (2 * r + dv) - dv + padding;
		ctx.setSize(w, h);
		let xStart = 0;
		const centerCircleX = padding / 2 + maxLeft * (2 * r + dh) - dh - r;
		let yStart = padding / 2;
		const circleLoc = [];
		arr.forEach((row, i) => {
			const numSpaces = row.length;
			xStart = centerCircleX - (numSpaces * (2 * r + dh)) / 2 + dh / 2 + r;
			yStart += i === 0 ? r : 2 * r + dv;
			circleLoc.push([]);
			row.forEach(content => {
				circleLoc[i].push([xStart, yStart, content !== null]);
				xStart += 2 * r + dh;
			});
		});

		//stores labels
		const labels = settings.labels || null;

		circleLoc.forEach((row, i) => {
			const nextRow = circleLoc[i + 1];
			row.forEach((col, k) => {
				if (nextRow) {
					if (col[2]) {
						ctx.save();
						ctx.lineWidth = settings.thickness || 3;
						if (!_.isArray(arr[i][k])) {
							if (nextRow[k][2] && nextRow[k + 1][2]) {
								ctx
									.bp()
									.M(nextRow[k][0], nextRow[k][1])
									.L(col[0], col[1])
									.L(nextRow[k + 1][0], nextRow[k + 1][1])
									.stroke(colors.defaultblack);
							} else if (nextRow[k][2] && settings.lines === "all") {
								ctx
									.bp()
									.M(nextRow[k][0], nextRow[k][1])
									.L(col[0], col[1])
									.stroke(colors.defaultblack);
							} else if (nextRow[k + 1][2] && settings.lines === "all") {
								ctx
									.bp()
									.M(nextRow[k + 1][0], nextRow[k + 1][1])
									.L(col[0], col[1])
									.stroke(colors.defaultblack);
							}
						} else if (_.last(arr[i][k])) {
							for (let c = 0; c < _.last(arr[i][k]); c++) {
								ctx
									.bp()
									.M(nextRow[k + c][0], nextRow[k + c][1])
									.L(col[0], col[1])
									.stroke(colors.defaultblack);
							}
						}
						ctx.restore();
					}
				}
				const content = arr[i][k];
				if (content !== null) {
					let batex = content;
					if (_.isArray(content)) {
						batex = _.head(content);
					}
					//Draw the circles
					ctx.save();
					ctx.lineWidth = settings.circleThickness || 2;
					ctx
						.bp()
						.arc(col[0], col[1], r, 0, 2 * Math.PI)
						.stroke(colors.defaultblack)
						.fill(colors.white);
					ctx.restore();
					ctx.fillStyle = cs.defaultText;
					if (content !== "") {
						const rect = [col[0] - 0.8 * r, col[1] - r / 2.5, 1.6 * r, 0.8 * r];
						CanvasMath.drawInRect(
							ctx,
							Common.getCanvasMathProps(ctx, {
								batex: "$" + batex + "$",
								rect: rect,
								scaleDownOnly: true,
							})
						);

						//Fills in label
						if (labels && labels[i][k]) {
							const labelRect = [
								col[0] - 0.5 * r,
								col[1] + r / 2.5,
								r,
								0.4 * r,
							];
							CanvasMath.drawInRect(
								ctx,
								Common.getCanvasMathProps(ctx, {
									batex: "$" + labels[i][k] + "$",
									rect: labelRect,
									scaleDownOnly: true,
								})
							);
						}
					}
				}
			});
		});
		ctx.restore();
	},

	/**
	 * Draws a dieNet with provided contents in the square locations
	 * format: "o----;oooo;o----" o indicates square and - indicated not part of net, ; indicates end of row
	 * netContents: an array that should match the number of os in format that specifies what Batex is in each square
	 **/
	dieNet: function(ctx, format, netContents) {
		//Turn format & net contents into an array of stuff to draw
		const grid = _.map(format.split(";"), rowStr => rowStr.split(""));
		let onContent = 0;
		for (let i = 0; i < grid.length; i++) {
			for (let j = 0; j < grid[i].length; j++) {
				if (grid[i][j] === "o") {
					grid[i][j] = netContents[onContent];
					onContent++;
				} else {
					grid[i][j] = null;
				}
			}
		}
		const cellSize = 60;
		const padding = cellSize / 4;
		ctx.save();
		ctx.setSize(
			cellSize * grid[0].length + 2 * padding,
			cellSize * grid.length + 2 * padding
		);
		//Loop through and render cell contents
		ctx.lineCap = "round";
		const lwNorm = 2;
		const lwEdge = 3;
		for (let i = 0; i < grid.length; i++) {
			for (let j = 0; j < grid[i].length; j++) {
				if (grid[i][j] !== null) {
					const x = padding + cellSize * j;
					const y = padding + cellSize * i;
					Common.drawBatexAtCenter(
						ctx,
						grid[i][j],
						x + cellSize / 2,
						y + cellSize / 2
					);
					//Draw this cell's lines
					ctx
						.bp()
						.M(x, y)
						.L(x + cellSize, y)
						.stroke(null, !_.get(grid, [i - 1, j]) ? lwEdge : lwNorm);
					ctx
						.bp()
						.M(x, y)
						.L(x, y + cellSize)
						.stroke(null, !_.get(grid, [i, j - 1]) ? lwEdge : lwNorm);
					if (!_.get(grid, [i, j + 1])) {
						ctx
							.bp()
							.M(x + cellSize, y)
							.L(x + cellSize, y + cellSize)
							.stroke(null, lwEdge);
					}
					if (!_.get(grid, [i + 1, j])) {
						ctx
							.bp()
							.M(x, y + cellSize)
							.L(x + cellSize, y + cellSize)
							.stroke(null, lwEdge);
					}
				}
			}
		}
		ctx.restore();
	},

	/**
	 * num is the number to be turned into a dot diagram.
	 * base is the base (10 for decimal, 2 for binary, etc.)
	 * color is the color of the dots
	 * diffColor is the color of the dot in the upper-right corner of the grid of dots specified by diffDigit
	 * label is an optional title to the diagram of color labelColor. It goes below the graph with a line between.
	 **/
	dotDiagram: function(ctx, num, settings = {}) {
		settings = _.extend(
			{
				base: 10,
				color: colors.defaultblack,
				r: 2,
				diffColor: null,
				diffDigit: 0,
				labelColor: colors.defaultblack,
			},
			settings
		);
		num = "" + num;
		const {base, color, r, diffColor, diffDigit, label, labelColor} = settings;
		const inGridDim = 4 * r;
		const betweenGridSame = 3 * r;
		const betweenGridDiff = 3 * r;
		let xStart = r + 3;
		const labelHeight = 10 * r;
		const yStart =
			r + inGridDim * Math.pow(base, Math.ceil((num.length - 1) / 2));
		const drawDigit = function(digit, pow) {
			const widthDots = Math.pow(base, Math.floor(pow / 2));
			const heightDots = pow % 2 === 1 ? widthDots * base : widthDots;
			for (let j = 0; j < digit; j++) {
				for (let x = 0; x < widthDots; x++) {
					for (let y = 0; y < heightDots; y++) {
						ctx
							.bp()
							.arc(
								xStart + x * inGridDim,
								yStart - y * inGridDim - r,
								r,
								0,
								2 * Math.PI
							);
						if (
							num.length - pow !== diffDigit ||
							j + 1 !== digit ||
							x + 1 !== widthDots ||
							y + 1 !== heightDots
						) {
							ctx.stroke(color).fill(color);
						} else {
							ctx.stroke(diffColor).fill(diffColor);
						}
					}
				}
				xStart += widthDots * inGridDim + betweenGridSame;
			}
		};

		let width = 3 - (betweenGridDiff + betweenGridSame);
		for (let x = num.length - 1, tempBase = 1; x >= 0; x--) {
			width += (num.charAt(x) === "0" ? 0 : 1) * betweenGridDiff;
			width +=
				parseInt(num.charAt(x)) * (betweenGridSame + tempBase * inGridDim);
			if ((num.length - x) % 2 === 0) {
				tempBase *= base;
			}
		}
		const height =
			2 * r + Math.pow(base, Math.ceil((num.length - 1) / 2)) * inGridDim;
		ctx.setSize(width, height + (!label ? 0 : labelHeight * 1.5));
		ctx.save();
		_.each(num, (digit, i) => {
			if (digit !== "0") {
				drawDigit(parseInt(digit), num.length - i - 1);
				xStart += betweenGridDiff;
			}
		});
		if (label) {
			ctx
				.bp()
				.M(0, height + labelHeight * 0.25)
				.H(width)
				.stroke(colors.defaultblack);
			ctx.fillStyle = labelColor;
			Common.drawBatexInRect(
				ctx,
				label,
				0,
				height + labelHeight / 2,
				width,
				labelHeight
			);
		}
		ctx.restore();
	},

	/**
	 * arr is a number that needs to be factored into prime factors or a tree array.
	 * settings holds whether the circles should be big.
	 **/
	primeFactors: function(ctx, input, settings = {}) {
		let primeTree;

		//If an integer was passed in, it must be converted to a tree array
		if (_.isInteger(input)) {
			const primes = [];
			//find the prime factors
			for (let i = 2; i <= input; i++) {
				while (input % i === 0) {
					primes.push(i);
					input /= i;
				}
			}
			// returns one of the shallowest trees possible given this prime factorization
			const treeify = function(arr) {
				if (arr.length === 1) {
					return [arr];
				} else {
					// treeLeft will always have depth >= that of treeRight in this construction
					const treeLeft = treeify(arr.slice(arr.length / 2, arr.length));
					const treeRight = treeify(arr.slice(0, arr.length / 2));

					const ret = [[treeLeft[0][0] * treeRight[0][0]]];

					_.each(treeLeft, (elem, i) => {
						// we might need to fill arrays will null placeholders
						const secondHalf =
							i < treeRight.length
								? treeRight[i]
								: _.fill(Array(treeLeft[i].length), null);
						ret.push(elem.concat(secondHalf));
					});

					return ret;
				}
			};

			primeTree = treeify(primes);
		} else {
			primeTree = input;
		}

		//Now we build the prime factor tree
		const r = settings.size === "large" ? 20 : 12; // each individual circle's radius
		const dh = r / 2; // horizontal space between circles
		const dv = r / 2; // vertical space between circles
		const isLeaf = function(x, y) {
			return (
				primeTree[x][y] != null &&
				(x === primeTree.length - 1 ||
					(primeTree[x + 1][2 * y] === null &&
						primeTree[x + 1][2 * y + 1] === null))
			);
		};

		const numPrimes =
			(_.filter(_.flatten(primeTree), x => x !== null).length + 1) / 2;

		ctx.save();
		const w = numPrimes * (2 * r + dh);
		const spaceBetween = 2 * r + (w - numPrimes * (2 * r) - dh) / numPrimes;
		let xStart = spaceBetween / 2;
		ctx.setSize(w, primeTree.length * (2 * r + dv));

		const locations = _.map(primeTree, x => _.fill(Array(x.length), [0, 0]));

		const alignLeaves = function(x, y, yStart) {
			if (isLeaf(x, y)) {
				locations[x][y] = [xStart, yStart];
				xStart += spaceBetween;
			} else if (primeTree[x][y] !== null) {
				alignLeaves(x + 1, 2 * y, yStart + (2 * r + dh));
				alignLeaves(x + 1, 2 * y + 1, yStart + (2 * r + dh));
				locations[x][y] = [
					(locations[x + 1][2 * y][0] + locations[x + 1][2 * y + 1][0]) / 2,
					yStart,
				];
			}
		};

		alignLeaves(0, 0, r + dh / 2);

		const padding = 10;

		locations.forEach((row, i) => {
			row.forEach((col, k) => {
				const batex = primeTree[i][k];
				if (!batex) return;

				if (isLeaf(i, k)) {
					//Draw a circle
					ctx
						.bp()
						.arc(col[0], col[1], r, 0, 2 * Math.PI)
						.stroke(colors.defaultblack)
						.fill(colors.white);
				} else {
					const child1 = locations[i + 1][2 * k];
					const child2 = locations[i + 1][2 * k + 1];
					// Drawing lines to children
					ctx
						.bp()
						.M(child1[0], child1[1] - padding)
						.L(col[0], col[1] + padding)
						.L(child2[0], child2[1] - padding)
						.stroke(colors.defaultblack);
				}

				ctx.fillStyle = cs.defaultText;
				const size = 20; //No matter the circle size, we want the size of the numbers to be constant
				Common.drawBatexInRect(
					ctx,
					"$" + batex + "$",
					col[0] - size,
					col[1] - size / 2,
					2 * size,
					size
				);
			});
		});
		ctx.restore();
	},

	/**
	 * vertices is an array of arrays, each of whose first element is an optional
	 * number or string drawn in the vertex and whose second element is the V
	 * object with the coordinates.
	 * regions is an array of arrays, each of whose first element is an optional
	 * number or string drawn in the region and whose remaining elements are the
	 * V objects with the coordinates.
	 **/
	regionSums: function(ctx, vertices, regions, radius) {
		radius = radius || 25;
		ctx.save();
		regions.forEach(rData => {
			const regionVertices = rData.slice(1);
			if (!regionVertices.length) {
				return;
			}
			const text = rData[0] || rData[0] === 0 ? "" + rData[0] : null;
			ctx.beginPath();
			regionVertices.forEach(v => ctx.lineTo(v));
			ctx.closePath();
			ctx.stroke(null, 2);
			if (text) {
				const centroid = V.sum(...regionVertices).scale(
					1 / regionVertices.length
				);
				Common.drawBatexAtCenter(ctx, text, centroid[0], centroid[1]);
			}
		});
		vertices.forEach(vData => {
			if (!vData[1]) {
				return;
			}
			const text = vData[0] || vData[0] === 0 ? "" + vData[0] : null;
			const x = vData[1][0];
			const y = vData[1][1];
			const oldFill = ctx.fillStyle;
			ctx.beginPath();
			ctx.arc(x, y, radius, 0, 2 * Math.PI);
			ctx.fill(0xffffff).stroke();
			ctx.fillStyle = oldFill;
			if (text) {
				Common.drawBatexAtCenter(ctx, text, x, y);
			}
		});
		ctx.restore();
	},

	/**
   *  Creates a horizontal tree with each column labeled
   *  labels - array of the column labels (as string)
   *  treeData - array of list (array) of options in each column. Automatically creates same number of branches for each option. If option is an array [label, color], the label and its children will be that color.
   *  customFinal - a list of final outcomes, if null, labels 1 to x number of possibilities.
   If customFinal is "permute", then final outcomes is automated list of permutations of options.
   * isCombo - if true, it will autopopulate treedata given options in each column.
   **/
	autoHorizontalTree: function(ctx, labels, treeData, customFinal, isCombo) {
		let totalChoices;
		let final = [];
		let cols = [];
		const hasFinal = customFinal !== "none";
		if (isCombo) {
			const opts = _.cloneDeep(treeData);
			const build = (opt, origPart, level) => {
				const part = _.clone(origPart);
				if (!opt.length) {
					final.push(part);
				} else {
					opt.forEach(o => {
						const dataSet = _.union(opt, treeData[level + 1]);
						const filtered = dataSet.filter(v => v !== o);
						if (!cols[level]) {
							cols[level] = [o];
						} else {
							cols[level] = cols[level].concat([o]);
						}
						build(filtered, part.concat([o]), level + 1);
					});
				}
			};
			build(opts[0], [], 0);
			totalChoices = final.length;
			if (hasFinal) {
				final = _.isArray(customFinal)
					? customFinal
					: final.map(f => f.join(""));
				cols = cols.concat([final]);
			}
		} else {
			// Gets the number of times choice needs to be repeated
			// in entire list of permutations.
			// column is the index to start from in treeData.
			const findNumChoices = function(column) {
				const dataToUse = treeData.slice(column);
				return _.reduce(dataToUse, (total, n) => n.length * total, 1);
			};
			totalChoices = findNumChoices(0);
			if (customFinal === "permute") {
				customFinal = _.fill(Array(totalChoices), "");
				_.forEach(treeData, (val, key) => {
					const numToRepeat = findNumChoices(key + 1);
					const numToCycle = totalChoices / numToRepeat / val.length;
					let i = 0;
					for (let cycle = 0; cycle < numToCycle; cycle++) {
						val.forEach(opt => {
							const option = _.isArray(opt) ? opt[0] : opt;
							for (let rep = 0; rep < numToRepeat; rep++) {
								customFinal[i] += option;
								i++;
							}
						});
					}
				});
			} else if (hasFinal) {
				customFinal = customFinal || _.range(1, totalChoices + 1);
			}
			if (hasFinal) {
				treeData = treeData.concat([customFinal]);
			}
		}
		const fontSize = Font.getSize(ctx.font);
		const mult = 0.85;
		const itemFont = Font.scaleSize(ctx.font, mult);
		const itemFontSize = fontSize * mult;
		const colPadding = fontSize * 3.5;
		const rowPadding = itemFontSize * 0.5;
		const leftPadding = fontSize / 2;
		const topPadding = fontSize / 2;
		const labelFontObj = _.extend(Font.toInfo(ctx.font), {bold: true});
		const itemFontObj = _.extend(Font.toInfo(ctx.font), {size: itemFontSize});
		const maxItemWidth = [];

		//Find max column width for all columns
		const tree = isCombo ? cols : treeData;
		const maxColWidth = tree.map((t, i) => {
			// let w = labels[i] ? Common.measureBatex(ctx, labels[i].toString()).width : 0;
			const labelWidth = labels[i]
				? Common.measureBatex(ctx, labels[i].toString(), labelFontObj).width
				: 0;
			let itemWidth = 0;
			t.forEach(l => {
				const toMeasure = _.isArray(l) ? l[0] : l;
				if (toMeasure) {
					itemWidth = Math.max(
						itemWidth,
						Common.measureBatex(ctx, toMeasure.toString(), itemFontObj).width
					);
				}
			});
			maxItemWidth.push(itemWidth);
			return Math.max(labelWidth, itemWidth);
		});

		const totalWidth =
			_.reduce(maxColWidth, (sum, m) => sum + m, leftPadding) +
			colPadding * (labels.length - (hasFinal ? 0 : 1));
		const totalHeight =
			topPadding + fontSize + totalChoices * (itemFontSize + rowPadding);

		ctx.setSize(totalWidth + fontSize, totalHeight + fontSize);

		let startX = leftPadding;
		const colStartX = maxColWidth.map((w, i) => {
			if (i === maxColWidth.length - 1) {
				startX += maxColWidth[i - 1] + colPadding / 2;
			} else if (i !== 0) {
				startX += maxColWidth[i - 1] + colPadding;
			}
			return startX;
		});

		//Draw main column labels
		labels.forEach((colLabel, i) => {
			const labelX =
				colStartX[i] +
				(i === 0
					? maxColWidth[0] -
					  Common.measureBatex(ctx, labels[i].toString(), labelFontObj).width
					: 0);
			Common.drawBatex(
				ctx,
				"\\bold{\\underline{" + colLabel + "}}",
				labelX,
				topPadding
			);
		});

		const headerHeight = topPadding + fontSize + rowPadding;
		const heightForItems = totalHeight - headerHeight;

		//Create an updated tree with all the items filled in each column
		let filledTree = [];
		if (!isCombo) {
			treeData.forEach((col, i) => {
				if (i === 0 || i === labels.length) {
					filledTree.push(col);
				} else {
					let newCol = [];
					//If the parent item has a color attached, make sure color info also in child item
					filledTree[i - 1].forEach(item => {
						let childItems = _.cloneDeep(col);
						if (_.isArray(item)) {
							childItems = childItems.map(c => [c, item[1]]);
						}
						newCol = newCol.concat(childItems);
					});
					filledTree.push(newCol);
				}
			});
		} else {
			filledTree = cols;
		}

		// If all items in a column are smaller than the label,
		// pad items in a column with (label width - max item width) / 2
		const colPaddings = maxColWidth.map((w, i) => {
			return (w - maxItemWidth[i]) / 2;
		});
		//Gets the end point of parent item
		const getParentLocation = function(i, j) {
			const parentIndex = Math.floor(
				j / Math.floor(filledTree[i].length / filledTree[i - 1].length)
			);
			const parentItem = filledTree[i - 1][parentIndex];
			const parentText = _.isArray(parentItem) ? parentItem[0] : parentItem;
			const x =
				colStartX[i - 1] +
				(i === 1
					? maxColWidth[0] - colPaddings[0]
					: Common.measureBatex(ctx, parentText, itemFontObj).width +
					  colPaddings[i - 1]);
			const eachH = heightForItems / filledTree[i - 1].length;
			const y = headerHeight + parentIndex * eachH + eachH / 2;
			return [x + itemFontSize / 2, y];
		};

		//Draw each item in the columns
		filledTree.forEach((col, i) => {
			const itemHeight = heightForItems / col.length;
			let startY = headerHeight + itemHeight / 2 - itemFontSize / 2;
			col.forEach((lab, j) => {
				ctx.save();
				const text = _.isArray(lab) ? lab[0] : lab;
				if (_.isArray(lab)) {
					ctx.fillStyle = lab[1];
					ctx.strokeStyle = lab[1];
				}
				ctx.font = itemFont;
				if (text) {
					const xStart =
						colStartX[i] +
						(i === 0
							? maxColWidth[0] -
							  Common.measureBatex(ctx, text.toString(), itemFontObj).width -
							  colPaddings[0]
							: colPaddings[i]);
					Common.drawBatex(ctx, text.toString(), xStart, startY);
				}
				//Draw the lines connecting items
				if (i > 0 && i < filledTree.length - 1) {
					const parentLocation = getParentLocation(i, j);
					ctx
						.bp()
						.M(
							colStartX[i] - itemFontSize / 2 + colPaddings[i],
							startY + itemFontSize / 2
						)
						.L(parentLocation[0], parentLocation[1])
						.stroke();
				}
				ctx.restore();
				startY += itemHeight;
			});
		});
	},
};
